import React, { useState } from 'react';
import {
  Close as CloseIcon,
  ShoppingCart as CartIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface CartItemData {
  id: string;
  orderId: string;
  siteId: string;
  siteUrl: string;
  siteDa: number;
  siteDr: number;
  siteTraffic: number;
  price: number;
  publisherName: string;
  publisherEmail: string;
  createdAt: string;
  updatedAt: string;
}

interface ProjectCartData {
  projectId: string;
  projectName: string;
  projectBudget: number;
  clientId: string;
  clientName: string;
  clientBudget: number;
  cartItems: CartItemData[];
  cartTotal: number;
  cartCount: number;
}

interface AllCartDrawerProps {
  open: boolean;
  onClose: () => void;
  projects: ProjectCartData[];
  totalItems: number;
  totalAmount: number;
  onRemoveItem?: (itemId: string) => Promise<void> | void;
  onRefresh?: () => void;
  onCheckout?: (projectId: string, projectName: string, clientName: string) => void;
}

const AllCartDrawer: React.FC<AllCartDrawerProps> = ({
  open,
  onClose,
  projects,
  totalItems,
  totalAmount,
  onRemoveItem,
  onRefresh,
  onCheckout,
}) => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [removingItems, setRemovingItems] = useState<Set<string>>(new Set());

  const handleViewProject = (projectId: string, clientId: string) => {
    navigate(`/sites?projectId=${projectId}&clientId=${clientId}`);
    onClose();
  };

  const handleCheckout = (project: ProjectCartData) => {
    if (onCheckout) {
      onCheckout(project.projectId, project.projectName, project.clientName);
    }
  };

  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(
    new Set(projects.length === 1 ? [projects[0]?.projectId] : [])
  );

  const isProjectFullySelected = (project: ProjectCartData): boolean => {
    if (!project.cartItems.length) return false;
    return project.cartItems.every((item) => selectedItems.has(item.id));
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleProjectSelection = (project: ProjectCartData) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      const allSelected = project.cartItems.every((item) => next.has(item.id));
      project.cartItems.forEach((item) => {
        if (allSelected) next.delete(item.id);
        else next.add(item.id);
      });
      return next;
    });
  };

  const removeSingleItem = async (itemId: string) => {
    if (!onRemoveItem) return;
    setRemovingItems((prev) => new Set(prev).add(itemId));
    try {
      await onRemoveItem(itemId);
      setSelectedItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    } finally {
      setRemovingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const removeSelectedItemsForProject = async (project: ProjectCartData) => {
    if (!onRemoveItem) return;
    const selectedProjectItemIds = project.cartItems
      .map((item) => item.id)
      .filter((id) => selectedItems.has(id));
    if (!selectedProjectItemIds.length) return;

    for (const itemId of selectedProjectItemIds) {
      await removeSingleItem(itemId);
    }
  };

  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  return (
    <>
      <style>{`
        .cart-drawer-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1300;
          display: ${open ? 'block' : 'none'};
        }
        .cart-drawer {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: 450px;
          max-width: 100%;
          background: #FFFFFF;
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
          z-index: 1301;
          transform: translateX(${open ? '0' : '100%'});
          transition: transform 0.3s ease-in-out;
          display: flex;
          flex-direction: column;
        }
        .cart-drawer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #E2E8F0;
        }
        .cart-drawer-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 18px;
          font-weight: 600;
          color: #1E293B;
        }
        .cart-drawer-close {
          padding: 8px;
          border: none;
          background: none;
          cursor: pointer;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
        }
        .cart-drawer-close:hover {
          background: #F1F5F9;
        }
        .cart-drawer-close svg {
          width: 20px;
          height: 20px;
          color: #64748B;
        }
        .cart-summary {
          padding: 12px 16px;
          background: #3B82F6;
          color: white;
          border-radius: 8px;
          margin: 0 20px 12px 20px;
        }
        .cart-summary-row {
          font-size: 12px;
          margin-bottom: 2px;
        }
        .cart-summary-total {
          font-size: 20px;
          font-weight: 600;
          margin: 4px 0;
        }
        .cart-summary-subtitle {
          font-size: 11px;
          opacity: 0.9;
        }
        .cart-drawer-content {
          flex: 1;
          overflow-y: auto;
          padding: 0 20px;
        }
        .cart-project {
          border: 1px solid #E2E8F0;
          border-radius: 6px;
          margin-bottom: 8px;
          overflow: hidden;
        }
        .cart-project-header {
          padding: 10px 12px;
          background: #F8FAFC;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: background 0.15s;
        }
        .cart-project-header:hover {
          background: #F1F5F9;
        }
        .cart-project-info {
          flex: 1;
          min-width: 0;
        }
        .cart-project-name {
          font-size: 14px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cart-project-client {
          font-size: 11px;
          color: #64748B;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cart-project-badge {
          padding: 3px 8px;
          background: #2563EB;
          color: white;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 500;
          margin-right: 6px;
          flex-shrink: 0;
        }
        .cart-project-price {
          font-size: 13px;
          font-weight: 600;
          color: #2563EB;
          margin-top: 2px;
          flex-shrink: 0;
        }
        .cart-project-expand {
          display: flex;
          align-items: center;
          transition: transform 0.2s;
        }
        .cart-project-expand.expanded {
          transform: rotate(180deg);
        }
        .cart-project-expand svg {
          width: 20px;
          height: 20px;
          color: #64748B;
        }
        .cart-project-items {
          padding: 8px 12px;
          display: none;
        }
        .cart-project-items.expanded {
          display: block;
        }
        .cart-item {
          border: 1px solid #E2E8F0;
          border-radius: 6px;
          padding: 8px;
          margin-bottom: 6px;
          background: #FFFFFF;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .cart-item-select {
          margin-right: 8px;
          display: flex;
          align-items: center;
        }
        .cart-item-info {
          flex: 1;
          min-width: 0;
        }
        .cart-item-url {
          font-size: 12px;
          font-weight: 500;
          color: #1E293B;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cart-item-stats {
          font-size: 10px;
          color: #64748B;
          margin-bottom: 2px;
          display: flex;
          gap: 6px;
          align-items: center;
          flex-wrap: wrap;
        }
        .cart-item-stats span {
          white-space: nowrap;
        }
        .cart-item-stats .cart-item-price {
          font-weight: 600;
          color: #2563EB;
          margin-left: auto;
        }
        .cart-item-delete {
          padding: 4px;
          border: none;
          background: none;
          cursor: pointer;
          border-radius: 3px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
          flex-shrink: 0;
        }
        .cart-item-delete:hover {
          background: #FEF2F2;
        }
        .cart-item-delete svg {
          width: 14px;
          height: 14px;
          color: #EF4444;
        }
        .cart-project-actions {
          display: flex;
          gap: 8px;
          margin-top: 12px;
        }
        .cart-project-bulk-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 8px;
          padding: 6px 0 8px 0;
          border-bottom: 1px dashed #E2E8F0;
        }
        .cart-bulk-select {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #475569;
          cursor: pointer;
        }
        .cart-bulk-remove {
          padding: 4px 8px;
          border-radius: 6px;
          border: 1px solid #FCA5A5;
          background: #FEF2F2;
          color: #B91C1C;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
        }
        .cart-bulk-remove:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .cart-btn {
          flex: 1;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          border: none;
          font-family: inherit;
        }
        .cart-btn-primary {
          background: #2563EB;
          color: white;
        }
        .cart-btn-primary:hover {
          background: white;
          color: #1E293B;
          border: 1px solid #E2E8F0;
        }
        .cart-btn-secondary {
          background: white;
          color: #475569;
          border: 1px solid #E2E8F0;
        }
        .cart-btn-secondary:hover {
          background: #F8FAFC;
        }
        .cart-drawer-footer {
          padding: 12px 20px;
          border-top: 1px solid #E2E8F0;
        }
        .cart-footer-text {
          font-size: 12px;
          color: #64748B;
          margin-bottom: 12px;
        }
        .cart-empty {
          text-align: center;
          padding: 60px 20px;
        }
        .cart-empty svg {
          width: 64px;
          height: 64px;
          color: #CBD5E1;
          margin-bottom: 16px;
        }
        .cart-empty-text {
          font-size: 14px;
          color: #64748B;
        }
      `}</style>
      <div className="cart-drawer-overlay" onClick={onClose} />
      <div className="cart-drawer">
        {/* Header */}
        <div className="cart-drawer-header">
          <div className="cart-drawer-title">
            <CartIcon style={{ color: '#2563EB' }} />
            <span>All Cart Items</span>
          </div>
          <button className="cart-drawer-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Summary */}
        <div className="cart-summary">
          <div className="cart-summary-row">
            Total Items: <strong>{totalItems}</strong>
          </div>
          <div className="cart-summary-total">
            Total: <strong>${totalAmount.toFixed(2)}</strong>
          </div>
          <div className="cart-summary-subtitle">
            Across {projects.length} project{projects.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Projects List */}
        <div className="cart-drawer-content">
          {projects.length === 0 ? (
            <div className="cart-empty">
              <CartIcon />
              <div className="cart-empty-text">No items in cart</div>
            </div>
          ) : (
            projects.map((project) => (
              <div key={project.projectId} className="cart-project">
                <div className="cart-project-header" onClick={() => toggleProject(project.projectId)}>
                  <div className="cart-project-info">
                    <div className="cart-project-name">{project.projectName}</div>
                    <div className="cart-project-client">{project.clientName}</div>
                    <div className="cart-project-price">${project.cartTotal.toFixed(2)}</div>
                  </div>
                  <span className="cart-project-badge">{project.cartCount} items</span>
                  <div className={`cart-project-expand ${expandedProjects.has(project.projectId) ? 'expanded' : ''}`}>
                    <ExpandMoreIcon />
                  </div>
                </div>
                <div className={`cart-project-items ${expandedProjects.has(project.projectId) ? 'expanded' : ''}`}>
                  {onRemoveItem && (
                    <div className="cart-project-bulk-actions">
                      <label className="cart-bulk-select">
                        <input
                          type="checkbox"
                          checked={isProjectFullySelected(project)}
                          onChange={() => toggleProjectSelection(project)}
                        />
                        <span>Select all ({project.cartItems.length})</span>
                      </label>
                      <button
                        className="cart-bulk-remove"
                        disabled={!project.cartItems.some((item) => selectedItems.has(item.id))}
                        onClick={() => removeSelectedItemsForProject(project)}
                      >
                        Remove selected
                      </button>
                    </div>
                  )}
                  {project.cartItems.map((item) => (
                    <div key={item.id} className="cart-item">
                      {onRemoveItem && (
                        <div className="cart-item-select">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(item.id)}
                            onChange={() => toggleItemSelection(item.id)}
                          />
                        </div>
                      )}
                      <div className="cart-item-info">
                        <div className="cart-item-url">{item.siteUrl}</div>
                        <div className="cart-item-stats">
                          DA: {item.siteDa} | DR: {item.siteDr} | Traffic: {item.siteTraffic.toLocaleString()}
                        </div>
                        <div className="cart-item-price">${item.price.toFixed(2)}</div>
                      </div>
                      {onRemoveItem && (
                        <button
                          className="cart-item-delete"
                          onClick={() => removeSingleItem(item.id)}
                          disabled={removingItems.has(item.id)}
                          title={removingItems.has(item.id) ? 'Removing...' : 'Remove item'}
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="cart-project-actions">
                    {onCheckout && (
                      <button className="cart-btn cart-btn-primary" onClick={() => handleCheckout(project)}>
                        Checkout
                      </button>
                    )}
                    <button className="cart-btn cart-btn-secondary" onClick={() => handleViewProject(project.projectId, project.clientId)}>
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer Actions */}
        {projects.length > 0 && (
          <div className="cart-drawer-footer">
            <div className="cart-footer-text">
              Click "Checkout" to process items or "View Details" to see more
            </div>
            {onRefresh && (
              <button className="cart-btn cart-btn-secondary" onClick={onRefresh} style={{ width: '100%' }}>
                Refresh Cart
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AllCartDrawer;
